export type StringBoolean = 'true' | 'false';

export interface ISeo {
	href: string;
}

export interface IPrice {
	currency: string;
	description: string;
	usage: string;
	value: string | number;
}

export interface IAttribute<T> {
	comparable: StringBoolean;
	displayable: StringBoolean;
	facetable: StringBoolean;
	id: string;
	identifier: string;
	key: string;
	merchandisable: StringBoolean;
	name: string;
	searchable: StringBoolean;
	storeDisplay: StringBoolean;
	usage: string;
	values: T[];
	swatchable: StringBoolean;
	uniqueID?: string;
}

export interface IInventory {
	attrval: string;
	attr: string;
	sku: string;
	value: string;
	thumbnail: string;
	fullImage: string;
	listPrice: string;
	offerPrice: string;
	taxablePrice: string;
	vatPercent: string;
	vatValue: string;
}

export interface IInventoryByProductId {
	inventory: { [id: string]: IInventory[] };
}

export interface IColorWithId {
	color: string;
	identifier: string;
	id: string;
	value: string;
}

export interface IAvailableValueItem {
	id: string;
	label: string;
	icon?: string;
	available?: boolean;
	price?: IPricesWithVat;
}

export interface IPricesWithVat {
	offerPrice: string;
	listPrice: string;
	taxablePrice: string;
	vatPercent: string;
	vatValue: string;
}

export interface IAvailableColorWithId extends IColorWithId {
	available: boolean;
	thumbnail: string;
	fullImage: string;
	availableCapacity: (string | undefined)[];
	listPrice: string;
	offerPrice: string;
	taxablePrice: string;
	vatPercent: string;
	vatValue: string;
}

export interface IContent {
	attributes?: IAttribute<IValue>[];
	buyable: string;
	hasSingleSKU: boolean;
	id: string;
	name: string;
	parentCatalogGroupID: string;
	partNumber: string;
	price: IPrice[];
	seo?: ISeo;
	longDescription: string;
	shortDescription: string;
	storeID: string;
	thumbnail: string;
	type: string;
}

interface IBreadCrumpTrailEntryView {
	label: string;
	seo: ISeo;
	type: string;
	value: string;
}

export interface IEntry {
	label: string;
	value: string;
	count: string;
	extendedData: {
		uniqueId: string;
	};
}

export interface IExtendedData {
	allValuesReturned: StringBoolean;
	allowMultipleValueSelection: StringBoolean;
	displaySequence: string;
	displayable: StringBoolean;
	fname: string;
	max_display: string;
	maximumValuesToDisplay: string;
	name: string;
	propertyId: string;
	propertyvalue: string;
	sortorder: string;
	zero_display: StringBoolean;
}

export interface IFacet {
	entry: IEntry[];
	extendedData: IExtendedData;
	name: string;
	value: string;
}

export interface IProductsByCategoryId {
	contents: IContent[];
	items: IItem[];
	total: number;
	facets: IFacet[];
}

export interface IColorWithId {
	color: string;
	identifier: string;
	id: string;
	value: string;
}

export interface IValueWithId {
	value: string;
	id: string;
	parsedIdentifier: string;
}

export interface IItem {
	attributes?: IAttribute<IValue>[];
	price: number;
	discount?: number;
	colors: IColorWithId[];
	availableCountries: IColorWithId[];
	availableValues: IValueWithId[];
	availableCapacity: IValueWithId[];
	additionalInfo?: {
		label: string;
		value: string;
	};
	colorAttributeId?: string;
	capacityAttributeId?: string;
	thumbnail: string;
	name: string;
	parentCatalogGroupID?: string | string[];
	uniqueID: string;
	shortDescription: string;
	longDescription: string;
	manufacturer?: string;
	itemType?: string | string[];
	isGiftCard?: boolean;
	isRelated?: boolean;
	isUpsell?: boolean;
	id?: string;
	vatPercent?: string;
	priceDisplay?: number;
	priceOffer?: number;
	preorder: boolean;
	preorderDate?: string;
	isDigitalProduct?: boolean;
	isItem?: boolean;
	isItemHBBInstallments?: boolean;
	planAvailable?: boolean;
	instalmentValue?: string;
	fullImage?: string;
	seo?: string;
	availability?: boolean;
	availableCapacities?: IAvailableValueItem[];
	availableColors?: IAvailableColorWithId[];
	inventory?: IInventoryByProductId['inventory'];
	place?: number;
	pickupOrderAvailable?: boolean;
	launchDate?: string;
	allItemType?: string[];
	parents?: string[];
	productTextInstallmentValue?: string;
	partNumber?: string;
}

export interface IFilterEntry extends IEntry {
	enabled: boolean;
	parsedValue: string;
}

export interface IFilter {
	entry: IFilterEntry[];
	extendedData: IExtendedData;
	name: string;
	value: string;
}

interface IModel extends IProductsByCategoryId {
	_contents: IContent[];
	_breadCrumpTrailEntryView: IBreadCrumpTrailEntryView[];
	facets: IFacet[];
	_total: number | null;
}

export interface IColorWithId {
	color: string;
	identifier: string;
	id: string;
	value: string;
}
export interface IAvailableColorWithId extends IColorWithId {
	available: boolean;
	thumbnail: string;
	fullImage: string;
	availableCapacity: (string | undefined)[];
	listPrice: string;
	offerPrice: string;
	taxablePrice: string;
	vatPercent: string;
	vatValue: string;
}

export interface IAvailableValueItem {
	id: string;
	label: string;
	icon?: string;
	available?: boolean;
	price?: IPricesWithVat;
}

export interface IPricesWithVat {
	offerPrice: string;
	listPrice: string;
	taxablePrice: string;
	vatPercent: string;
	vatValue: string;
}
export interface IProductComponents {
	availableColors: IAvailableColorWithId[];
	availableCapacity: IAvailableValueItem[];
	inventory: IInventoryByProductId['inventory'];
	product: IItem;
}
export interface IValueWithId {
	value: string;
	id: string;
	parsedIdentifier: string;
}

export interface IValue {
	id: string | string[];
	identifier: string | string[];
	sequence: string | string[];
	value: string | string[];
	image1: string | string[];
	image1path?: string | string[];
	uniqueID?: string;
	[key: string]: any;
}
export interface IItem {
	attributes?: IAttribute<IValue>[];
	price: number;
	discount?: number;
	colors: IColorWithId[];
	availableCountries: IColorWithId[];
	availableValues: IValueWithId[];
	availableCapacity: IValueWithId[];
	additionalInfo?: {
		label: string;
		value: string;
	};
	colorAttributeId?: string;
	capacityAttributeId?: string;
	thumbnail: string;
	name: string;
	parentCatalogGroupID?: string | string[];
	uniqueID: string;
	shortDescription: string;
	longDescription: string;
	manufacturer?: string;
	itemType?: string | string[];
	isGiftCard?: boolean;
	isRelated?: boolean;
	isUpsell?: boolean;
	id?: string;
	vatPercent?: string;
	priceDisplay?: number;
	priceOffer?: number;
	preorder: boolean;
	preorderDate?: string;
	isDigitalProduct?: boolean;
	isItem?: boolean;
	isItemHBBInstallments?: boolean;
	planAvailable?: boolean;
	instalmentValue?: string;
	fullImage?: string;
	seo?: string;
	availability?: boolean;
	availableCapacities?: IAvailableValueItem[];
	availableColors?: IAvailableColorWithId[];
	inventory?: IInventoryByProductId['inventory'];
	place?: number;
	pickupOrderAvailable?: boolean;
	launchDate?: string;
	allItemType?: string[];
	parents?: string[];
	productTextInstallmentValue?: string;
	partNumber?: string;
}

export interface IAttribute<T> {
	comparable: StringBoolean;
	displayable: StringBoolean;
	facetable: StringBoolean;
	id: string;
	identifier: string;
	key: string;
	merchandisable: StringBoolean;
	name: string;
	searchable: StringBoolean;
	storeDisplay: StringBoolean;
	usage: string;
	values: T[];
	swatchable: StringBoolean;
	uniqueID?: string;
}

export interface IProductById {
	item: IItem;
	items?: IProductByIdItem[];
	whatsInTheBox: IWhatsInTheBoxItem[];
	keyFeatures: IKeyFeaturesItem[];
	technicalSpecification: ITechnicalSpecification[];
	manufacturer: IManufacturer;
	deviceDimensions: IDeviceDimension;
	relatedProducts: IItem[];
}
export interface ITariffIncluded {
	planId: string;
}

export interface IDeviceDimension {
	height?: string;
	width?: string;
	depth?: string;
}

export interface IManufacturer {
	manufacturer?: string;
	partNumber?: {
		name?: string;
		code?: string;
	};
}

export interface ITechnicalSpecification {
	name: string;
	description?: string;
}

export interface IKeyFeaturesItem {
	title: string;
	value?: string;
	img?: string;
	id?: string;
}

export interface IWhatsInTheBoxItem {
	name: string;
	img?: string;
	visible?: boolean;
}
export interface IProductByIdItem {
	attributes: IAttribute<IValueWithoutArray>[];
	additionalService?: string;
	insurancePromotion?: boolean;
	planDetails: ITariffIncluded;
	buyable: string;
	fullImage: string;
	hasSingleSKU: boolean;
	id: string;
	name: string;
	parent: string;
	parentCatalogEntryID: string;
	parentCatalogGroupID: string;
	partNumber: string;
	productPartNumber: string;
	priceDisplay: number;
	priceOffer: number;
	priceDiscount?: string;
	seo: ISeo;
	shortDescription: string;
	storeID: string;
	thumbnail: string;
	type: string;
	uniqueID: string;
	additionalInfo?: string;
	colorId?: string;
	capacityId?: string;
	countryId?: string;
	availableValue?: IAvailableValueItem;
	valueId?: string;
	comParamId?: string;
	installmentPeriod?: string;
	installmentValue?: string;
	productTextInstallmentValue?: string;
	vatPercent?: string;
	preorder: boolean;
	preorderDate?: string;
	defItemType: string;
	duration: '12' | '24';
	parents: string[];
}

export interface IValueWithoutArray {
	id: string;
	identifier: string;
	sequence: string;
	value: string;
	image1: string;
	image1path?: string;
}

export const COLOR_ATTRIBUTE = 'DEF_Color';
export const CAPACITY_ATTRIBUTE = 'DEF_Capacity';
export const COUNTRY_ATTRIBUTE = 'DEF_Country';
export const GIFT_CARD_VALUES_ATTRIBUTE = 'DEF_Value';
export const PREORDER = 'PREORDER';
export const PREORDER_DATE = 'PREORDER_DATE';
export const MANUFACTURER_ATTRIBUTE = 'MANUFACTURER';
export const ITEM_TYPE_ATTRIBUTE = 'DEF_ITEM_TYPE';
export const DIGITAL_PRODUCT_ITEM_TYPE = 'DIGITAL_PRODUCT';
export const ITEM_TYPE = 'ITEM';
export const GIFT_CARD_ITEM_TYPE = 'GIFTCARD';

export const getAvailableColors = (attributtes: IAttribute<IValue>[]): IColorWithId[] => {
	const colors = attributtes.find((attributte) => attributte.identifier === COLOR_ATTRIBUTE)?.values?.[0];

	// Related products colors
	if (!!colors?.uniqueID) {
		return attributtes
			.find((attributte) => attributte.identifier === COLOR_ATTRIBUTE)
			?.values.map((color) => ({
				color: color.image1,
				id: color.uniqueID,
				identifier: color.identifier,
				value: color.value,
			})) as IColorWithId[];
	}

	if (colors) {
		if (Array.isArray(colors.image1)) {
			return colors.image1.map((color, index) => ({
				color,
				id: colors.id[index],
				identifier: colors.identifier[index],
				value: colors.value[index],
			}));
		} else if (!Array.isArray(colors.id) && !Array.isArray(colors.identifier))
			return [
				{ color: colors.image1, id: colors.id, identifier: colors.identifier, value: String(colors.value) },
			];
	}
	return [];
};

export const getAvailableCountries = (attributes: IAttribute<IValue>[]): IColorWithId[] => {
	const countriesValues = attributes.find((attribute) => attribute.identifier === COUNTRY_ATTRIBUTE)?.values;
	if (countriesValues && Array.isArray(countriesValues)) {
		return countriesValues.map((value) => {
			if (!Array.isArray(value.image1)) {
				return {
					color: String(value.image1),
					id: String(value.id),
					identifier: String(value.identifier),
					value: String(value.value),
				};
			}
			return {
				color: value.image1[0],
				id: value.id[0],
				identifier: value.identifier[0],
				value: String(value.value),
			};
		});
	}
	return [];
};

export const getAttributeValue = (content: IContent, atrributeIdentifier: string): string | string[] | undefined => {
	return content?.attributes?.find((attr) => attr.identifier === atrributeIdentifier)?.values[0].value;
};

export const getPartNumber = (items: any[] | undefined, itemId: string): string => {
	return (
		items?.find((catalogEntryViewItem) => {
			return (
				catalogEntryViewItem?.attributes?.find((attr: any) => attr.identifier === GIFT_CARD_VALUES_ATTRIBUTE)
					?.values[0].id === itemId
			);
		})?.partNumber || ''
	);
};

export const getAvailableValues = (attributtes: IAttribute<IValue>[], catalogEntryView?: any[]): IValueWithId[] => {
	const value = attributtes.find((attr) => attr.identifier === GIFT_CARD_VALUES_ATTRIBUTE)?.values;
	if (Array.isArray(value)) {
		return value.map((item) => {
			if (!Array.isArray(item.value) && !Array.isArray(item.id)) {
				return {
					value: item.value,
					id: item.id,
					parsedIdentifier: getPartNumber(catalogEntryView?.[0].items, item.id || ''),
				};
			} else {
				return {
					value: item.value[0],
					id: item.id[0],
					parsedIdentifier: getPartNumber(catalogEntryView?.[0].items, item.id[0] || ''),
				};
			}
		});
	}
	return [];
};

export const getAvailableCapacity = (attributtes: IAttribute<IValue>[]): IValueWithId[] => {
	const capacity = attributtes.find((attributte) => attributte.identifier === CAPACITY_ATTRIBUTE)?.values?.[0];
	// Related products capacity
	if (!!capacity?.uniqueID) {
		return attributtes
			.find((attributte) => attributte.identifier === CAPACITY_ATTRIBUTE)
			?.values.map((capacity) => ({
				value: capacity.value,
				id: capacity.uniqueID,
				parsedIdentifier: capacity.identifier,
			})) as IValueWithId[];
	}
	if (capacity) {
		if (Array.isArray(capacity.value)) {
			return capacity.value.map((singleCapacity, index) => ({
				value: singleCapacity,
				id: capacity.id[index],
				parsedIdentifier: singleCapacity,
			}));
		} else if (!Array.isArray(capacity.id))
			return [{ value: capacity.value, id: capacity.id, parsedIdentifier: '' }];
	}
	return [];
};

export const getPrice = (prices: IPrice[], type: string): number => {
	return Number(prices.find((price) => price.usage === type)?.value);
};

export const getItems = (contents: IContent[]): IItem[] => {
	return contents.map((item) => {
		const { name, thumbnail, id, price, attributes = [], longDescription, shortDescription, seo } = item;
		const colors = getAvailableColors(attributes);
		const availableCountries = getAvailableCountries(attributes);
		const itemType = getAttributeValue(item, ITEM_TYPE_ATTRIBUTE) as string;
		const isGiftCard = itemType === GIFT_CARD_ITEM_TYPE;
		const isDigitalProduct = itemType === DIGITAL_PRODUCT_ITEM_TYPE;
		const isItem = itemType === ITEM_TYPE || itemType?.includes(ITEM_TYPE);

		return {
			attributes,
			name,
			price: getPrice(price, 'Offer'),
			colors,
			availableCountries,
			availableValues: getAvailableValues(attributes),
			selectedColor: '',
			availableCapacity: getAvailableCapacity(attributes),
			additionalInfo: (() => {
				const capacities = getAvailableCapacity(attributes);
				if (capacities.length) {
					return {
						label: 'product-card.capacity',
						value: `${capacities.map((item, index) => `${index > 0 ? ' ' : ''}${item.value}`)}`,
					};
				}
			})(),
			colorAttributeId: attributes.find((attribute) => {
				if (isGiftCard || isDigitalProduct) {
					return attribute.identifier === COUNTRY_ATTRIBUTE;
				}
				return attribute.identifier === COLOR_ATTRIBUTE;
			})?.id,
			capacityAttributeId: attributes.find((attribute) => attribute.identifier === CAPACITY_ATTRIBUTE)?.id,
			thumbnail,
			uniqueID: id,
			longDescription,
			shortDescription,
			manufacturer: getAttributeValue(item, MANUFACTURER_ATTRIBUTE) as string,
			itemType,
			isGiftCard,
			preorder: (getAttributeValue(item, PREORDER) as string) === 'TRUE',
			preorderDate: getAttributeValue(item, PREORDER_DATE) as string,
			isDigitalProduct,
			isItem,
			seo: seo?.href,
		};
	});
};

const model: types.models.InputModel = {
	_contents: ['response.contents', []],
	_breadCrumpTrailEntryView: 'response.breadCrumpTrailEntryView',
	facets: 'response.facets',
	_total: 'response.total',
	total: (model: IModel) => model?._total ?? 0,
	contents: (model: IModel) => model?._contents ?? [],
	items: (model: IModel): IItem[] => {
		return getItems(model._contents ?? []);
	},
};

export default model;
